<template>
    <BaseTable
        data-cy="pedidos-table"
        row-key="id"
        dense
        table-class="tw:min-w-[1344px]"
        :columns="columns"
        :data="pedidos"
        :skeleton="loading"
        :skeleton-rows="5"
        :expanded-row-keys="expandedRowKeys"
        empty-text="No hay invitaciones para esta sesión"
    >
        <!-- Skeleton de las dos celdas con forma propia (toggle y botón) -->
        <template #skeleton-marcado>
            <div class="tw:h-4 tw:w-8 tw:bg-gray-200 tw:rounded-full tw:mx-auto"></div>
        </template>
        <template #skeleton-opciones>
            <div class="tw:h-9 tw:w-32 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
        </template>

        <template #cell-marcado="{ row }">
            <ToggleSwitch
                size="sm"
                :value="!!seleccionados[row.id]"
                :aria-label="`Seleccionar pedido ${row.id}`"
                :data-cy="`marcar-pedido-${row.id}`"
                @input="$emit('toggle-seleccion', row.id)"
            />
        </template>

        <template #cell-id="{ row }">
            <span class="tw:font-semibold tw:text-blue-600">{{ row.id }}</span>
        </template>

        <template #cell-email_taquillero="{ row }">
            {{ row.email_taquillero }}
        </template>

        <template #cell-usuario="{ row }">
            <span class="tw:flex tw:flex-col tw:items-start tw:gap-1">
                <span v-if="abonadoDe(row)" class="tw:text-gray-500">({{ abonadoDe(row) }})</span>
                <span v-if="row.email_comprador">{{ row.email_comprador }}</span>
                <Pill
                    size="sm"
                    :variant="row.email_comprador ? 'emerald' : 'slate'"
                    :data-cy="`estado-envio-${row.id}`"
                >
                    {{ row.email_comprador ? 'Enviada' : 'No enviadas' }}
                </Pill>
            </span>
        </template>

        <template #cell-qr="{ row }">
            {{ (row.qr_codes || []).length }}
        </template>

        <template #cell-fecha="{ row }">
            {{ row.fecha }}
        </template>

        <template #cell-metodo_pago="{ row }">
            <Pill size="sm" variant="amber">{{ formatearMetodoPago(row.metodo_pago) }}</Pill>
        </template>

        <template #cell-modalidad="{ row }">
            {{ formatearModalidad(row.modalidad) }}
        </template>

        <template #cell-concepto="{ row }">
            <Pill v-if="conceptoDe(row)" size="sm" :variant="varianteConcepto(row)">{{ conceptoDe(row) }}</Pill>
        </template>

        <template #cell-precio="{ row }">
            <span v-if="row.anulado" class="tw:font-semibold tw:text-red-600">ANULADO</span>
            <span v-else>{{ formatearPrecio(row.precio) }}</span>
        </template>

        <template #cell-opciones="{ row }">
            <Button
                :variant="expandedKey === row.id ? 'primary' : 'default'"
                icon="fas fa-ticket-alt"
                :data-cy="`ver-entradas-${row.id}`"
                @click="$emit('toggle-expand', row.id)"
            >
                Entradas ({{ (row.entradas || []).length }})
            </Button>
        </template>

        <!--
          Desglose del pedido. Las entradas ya vienen dentro del pedido, así que
          no hay fetch ni loader propio: el skeleton del primer nivel ya cubrió
          la carga.
        -->
        <template #row-expanded="{ row }">
            <div class="tw:bg-slate-50 tw:px-5 tw:py-4">
                <PedidoEntradasTable :entradas="row.entradas || []" :pedido="row" />
            </div>
        </template>
    </BaseTable>
</template>

<script setup>
    import { computed } from 'vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import Pill from '@/modules/admin/ui/Pill.vue';
    import PedidoEntradasTable from '@/modules/admin/events/sesiones/components/invitaciones/PedidoEntradasTable.vue';
    import filters from '@/filters';

    const props = defineProps({
        columns: {
            type: Array,
            required: true
        },
        pedidos: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        seleccionados: {
            type: Object,
            default: () => ({})
        },
        expandedKey: {
            type: [String, Number],
            default: null
        }
    });

    defineEmits(['toggle-seleccion', 'toggle-expand']);

    // `BaseTable` (master) recibe las claves abiertas como array; la vista sigue
    // trabajando con un único pedido abierto, así que se envuelve acá.
    const expandedRowKeys = computed(() =>
        props.expandedKey == null ? [] : [props.expandedKey]
    );

    const formatearPrecio = value => filters.formatear_precio(value);
    const formatearMetodoPago = value => filters.formatear_metodo_pago_entrada(value);
    const formatearModalidad = value => filters.formatear_modalidad(value);

    /** Nº de abonado del primer ticket, cuando el pedido viene de un abonado. */
    const abonadoDe = pedido =>
        pedido.entradas && pedido.entradas[0] && pedido.entradas[0].abonado
            ? pedido.entradas[0].abonado.n_abonado
            : null;

    /** Concepto del primer ticket del pedido. */
    const conceptoDe = pedido =>
        pedido.entradas && pedido.entradas.length > 0 ? pedido.entradas[0].concept : '';

    /**
     * Color de la pastilla del concepto. Es una ayuda visual, no un contrato:
     * el concepto es texto libre del backend, así que se reconoce por palabra
     * clave y cae en `neutral` con cualquier otro valor.
     */
    function varianteConcepto(pedido) {
        const concepto = conceptoDe(pedido).toLowerCase();

        if (concepto.includes('protocolo')) return 'blue';
        if (concepto.includes('jugador')) return 'emerald';
        if (concepto.includes('acreditacion') || concepto.includes('acreditación')) return 'violet';
        if (concepto.includes('prensa')) return 'cyan';

        return 'slate';
    }
</script>
